/**
 * Dynamic per-sensor **track-tail** layer (issue #278 follow-up, PR1) —
 * a smooth line behind every sensor showing its recent path, trimmed to
 * the operator-configured km. Non-visual React component (like
 * :func:`SnapshotLayer`) mounted by :file:`Map.tsx`.
 *
 * Responsibilities:
 *   1. feed the track store from the live status SSE deltas
 *      (:func:`useTrackSseBridge`);
 *   2. render the store's tails, trimmed to the configured km, gated on
 *      the ``enabled`` preference, with the selected sensor's tail
 *      suppressed (it shows the richer full selected-trail instead);
 *   3. update the MapLibre source in place (no re-attach churn) — and
 *      detach when the feature is disabled.
 *
 * Reads the config via :func:`useDynamicTrack` (the persisted
 * ``map_settings.dynamic_track`` preference); changing the km in the
 * settings modal re-trims every tail on the next render.
 *
 * Cross-references:
 *   - REQ-UI-012 (trail precedent) / REQ-UI-051 (selected-trail suppress).
 *   - ADR-016 §5 + Open Item 12 (visual default).
 */
import { useEffect } from "react";
import type { Map as MapLibreMap } from "maplibre-gl";
import type { SseSubscribe } from "@/map/sse-subscription";
import { useAllTracks } from "@/map/track-store";
import { useTrackSseBridge } from "@/map/use-track-sse-bridge";
import { useDynamicTrack } from "@/map/use-dynamic-track";
import {
  attachTrackTailLayer,
  detachTrackTailLayer,
  tracksToFeatureCollection,
} from "@/map/track-tail-layer-impl";

/** Props for :func:`TrackTailLayer`. */
export interface TrackTailLayerProps {
  /** Live MapLibre map instance (mounted after the map's ``load``). */
  map: MapLibreMap;
  /** The Map's ``connection.subscribe`` (drives accumulation). */
  subscribe?: SseSubscribe | null;
}

export function TrackTailLayer({ map, subscribe = null }: TrackTailLayerProps): null {
  // Accumulate live positions even when tails are hidden, so toggling the
  // feature on shows the recent path immediately.
  useTrackSseBridge(subscribe);

  const byProducer = useAllTracks();
  const { config } = useDynamicTrack();

  // Detach once on unmount (or a rare ``map`` change).
  useEffect(() => {
    return () => {
      detachTrackTailLayer(map);
    };
  }, [map]);

  // Render / re-trim / enabled-gate. Idempotent attach updates the source
  // in place; when disabled, the layer is detached (hidden). Every sensor —
  // including the selected one — gets a live tail so the selected platform's
  // track keeps updating as it moves (the static REQ-UI-012 trail stands
  // down while this feature is enabled; see trail-layer.tsx).
  useEffect(() => {
    if (!config.enabled) {
      detachTrackTailLayer(map);
      return;
    }
    attachTrackTailLayer(
      map,
      tracksToFeatureCollection(byProducer, {
        lengthKm: config.length_km,
      }),
    );
  }, [map, byProducer, config.enabled, config.length_km]);

  return null;
}
